import { FC } from "react";
import { Box, ChakraUnorderedList, ChakraListItem } from "@hightouch/htui";
import { useFlags } from "launchdarkly-react-client-sdk";

import { useRealtimeAudiencesEnabled } from "src/hooks/use-realtime-audiences-enabled";
import { LinkButton, Navigate, NoContextOutlet } from "src/router";
import type { RouteObject } from "src/router";

import { routes as sourceRoutes } from "./sources";
import { routes as destinationRoutes } from "./destinations";
import { routes as syncRoutes } from "./syncs";
import { routes as contractRoutes } from "./contracts";
import { routes as eventLibraryRoutes } from "./event-library";
import { routes as eventStudioRoutes } from "./event-studio";
import { routes as functionRoutes } from "./functions";

import { useEntitlements } from "src/hooks/use-entitlement";
import { useUser } from "src/contexts/user-context";
import {
  HightouchRegions,
  getHightouchRegionConfig,
  getRegionFullName,
} from "src/utils/regions";
import { SplashPage } from "src/components/splash-page";

const EventsLayout: FC = () => {
  const { hasFullEvents, hasRealtimeOnly } = useEventsEntitlements();

  const showEvents = hasFullEvents || hasRealtimeOnly;

  const { region } = useUser();
  const regionConfig = getHightouchRegionConfig(region);

  if (!showEvents) {
    return <Navigate to="/" replace />;
  }

  if (!regionConfig.events.enabled) {
    return (
      <SplashPage
        hideLogos
        eyebrow="Event Collection"
        heading="Event Collection is not available in this region"
        description={
          <>
            <span>Hightouch Events is available in the following regions:</span>
            <ChakraUnorderedList mt={2} spacing={1}>
              {Object.entries(HightouchRegions)
                .filter(([_region, config]) => config.events.enabled)
                .map(([region, config]) => (
                  <ChakraListItem key={region}>
                    {getRegionFullName(config)}
                  </ChakraListItem>
                ))}
            </ChakraUnorderedList>
          </>
        }
        actions={
          <>
            <LinkButton href="/workspaces">View workspaces</LinkButton>
          </>
        }
        visual={
          <Box
            as="video"
            autoPlay
            muted
            playsInline
            src="https://cdn.sanity.io/files/pwmfmi47/production/721f7c8c41f06ef7df94444d3beae52c5d99814a.mp4"
            maxHeight="100%"
            width="100%"
          />
        }
      />
    );
  }

  return <NoContextOutlet />;
};

const useEventsEntitlements = () => {
  const { enableRealtimeAudiencesSetup } = useFlags();
  const realtimeAudiencesEnabled = useRealtimeAudiencesEnabled();
  const { data: entitlementsData } = useEntitlements(true, true);

  const hasFullEvents = entitlementsData.entitlements.events;
  const hasRealtimeOnly =
    !hasFullEvents &&
    (realtimeAudiencesEnabled || enableRealtimeAudiencesSetup);

  return { hasFullEvents, hasRealtimeOnly };
};

const RestrictedRoute: FC = () => {
  const { hasRealtimeOnly } = useEventsEntitlements();

  if (hasRealtimeOnly) {
    return <Navigate to="/events/sources" replace />;
  }

  return <NoContextOutlet />;
};

const RestrictedIndexRoute: FC = () => {
  const { hasRealtimeOnly } = useEventsEntitlements();

  return <Navigate to={hasRealtimeOnly ? "sources" : "syncs"} replace />;
};

// Event Studio replaces the Contracts UI. Gated behind the `enableEventStudio` flag;
// when off, fall back to the Contracts surface.
const EventStudioRoute: FC = () => {
  const { hasRealtimeOnly } = useEventsEntitlements();
  const { enableEventStudio } = useFlags();

  if (hasRealtimeOnly || !enableEventStudio) {
    return <Navigate to="/events/contracts" replace />;
  }

  return <NoContextOutlet />;
};

export const routes: Array<RouteObject> = [
  {
    path: "events",
    spacesAccess: "hidden-from-limited-view",
    element: <EventsLayout />,
    children: [
      {
        path: "sources",
        spacesAccess: "hidden-from-limited-view",
        children: sourceRoutes,
      },
      {
        path: "destinations",
        spacesAccess: "hidden-from-limited-view",
        element: <RestrictedRoute />,
        children: destinationRoutes,
      },
      {
        path: "syncs",
        spacesAccess: "hidden-from-limited-view",
        element: <RestrictedRoute />,
        children: syncRoutes,
      },
      {
        path: "contracts",
        spacesAccess: "hidden-from-limited-view",
        element: <RestrictedRoute />,
        children: contractRoutes,
      },
      {
        path: "library",
        spacesAccess: "hidden-from-limited-view",
        element: <EventStudioRoute />,
        children: eventLibraryRoutes,
      },
      {
        path: "domains",
        spacesAccess: "hidden-from-limited-view",
        element: <EventStudioRoute />,
        children: eventStudioRoutes,
      },
      {
        path: "functions",
        spacesAccess: "hidden-from-limited-view",
        element: <RestrictedRoute />,
        children: functionRoutes,
      },
      {
        index: true,
        spacesAccess: "hidden-from-limited-view",
        element: <RestrictedIndexRoute />,
      },
    ],
  },
];
